/**
 * Reminder data layer — TanStack Query hooks over the gated reminders API.
 * This is the ONLY place the reminders layer talks to the server; the
 * rendering components (the drawer's Reminders tab + the team bell) consume
 * these hooks and never call `apiFetch` directly — the same query ⟂ view
 * split the rest of taskmap keeps (§A7).
 *
 * Issue #768 (Phase 4, B4.2). A reminder is a **forward nudge** — "nudge about
 * this on <date>" — never a record of when something happened, never a tally
 * about a person (§A1.3 / §A9). The backend rejects a past `remind_at` (422);
 * the compose UI validates client-side too with a calm message.
 *
 * Two read scopes, both soonest-first as the endpoint returns:
 *   - **node-scoped** — `GET /tasks/nodes/{nodeId}/reminders`, keyed
 *     `["task-reminders", teamId, nodeId]`; the list a node's drawer shows.
 *   - **team bell** — `GET /tasks/reminders`, keyed `["task-reminders", teamId]`
 *     (no nodeId); the B4.2 bell list of every upcoming nudge.
 * Both mutations (set + clear) invalidate BOTH scopes so the bell and the
 * node list re-derive from one fresh source of truth after any change.
 *
 * Gating: these hooks are only *consumed* behind `task_reminders` (off by
 * default, §A10); the backend enforces the gate too. The queries are `enabled`
 * only when the surface is actually showing, and degrade calmly on a 404
 * (`retry: false`, empty list) so an undeployed/ungated endpoint never crashes.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling, no rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

/** One reminder, as it arrives from the API. */
export type TaskReminder = components["schemas"]["TaskReminderRead"];
type ReminderListResponse = components["schemas"]["ReminderListResponse"];
type ReminderResponse = components["schemas"]["ReminderResponse"];
type CreateReminderRequest = components["schemas"]["CreateReminderRequest"];

/**
 * Cache key for the reminders layer. With a `nodeId` it's one node's list; with
 * `nodeId` omitted it's the team bell. Exported for tests + invalidation.
 */
export const TASK_REMINDERS_KEY = (teamId: string, nodeId?: string): readonly string[] =>
  nodeId ? ["task-reminders", teamId, nodeId] : ["task-reminders", teamId];

/** Invalidate both reminder scopes (the node list + the team bell). */
const TASK_REMINDERS_SCOPE = (teamId: string) => ["task-reminders", teamId] as const;

const STALE_TIME_MS = 30_000;

function teamRemindersUrl(teamId: string): string {
  return `/api/teams/${teamId}/tasks/reminders`;
}

function nodeRemindersUrl(teamId: string, nodeId: string): string {
  return `/api/teams/${teamId}/tasks/nodes/${nodeId}/reminders`;
}

export interface UseRemindersResult {
  /** The reminders, soonest `remind_at` first (as the endpoint returns). */
  readonly reminders: TaskReminder[];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed / ungated 404). */
  readonly isError: boolean;
}

function toResult(query: {
  data?: ReminderListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}): UseRemindersResult {
  const reminders = Array.isArray(query.data?.reminders) ? query.data.reminders : [];
  return { reminders, isLoading: query.isLoading, isError: query.isError };
}

/**
 * Fetch ONE node's reminders (the drawer's Reminders tab). `enabled` only when
 * both ids are present (i.e. the tab is open AND the layer is on) so we never
 * fire a request for a closed drawer. Degrades to an empty list on a 404.
 */
export function useNodeReminders(
  teamId: string | undefined,
  nodeId: string | undefined,
): UseRemindersResult {
  const query = useQuery({
    queryKey: TASK_REMINDERS_KEY(teamId ?? "", nodeId ?? ""),
    enabled: Boolean(teamId) && Boolean(nodeId),
    staleTime: STALE_TIME_MS,
    // No retry: a not-yet-deployed / ungated endpoint should fail fast to the
    // calm "unavailable" state rather than spinning.
    retry: false,
    queryFn: () =>
      apiFetch<ReminderListResponse>(nodeRemindersUrl(teamId as string, nodeId as string)),
  });
  return toResult(query);
}

/**
 * Fetch the TEAM's reminders (the B4.2 bell list). `enabled` only when the
 * team id is present AND the caller opts in (`active` — e.g. the bell panel is
 * open and the layer is on), so no request fires for a closed bell. Degrades to
 * an empty list on a 404.
 */
export function useTeamReminders(teamId: string | undefined, active: boolean): UseRemindersResult {
  const query = useQuery({
    queryKey: TASK_REMINDERS_KEY(teamId ?? ""),
    enabled: Boolean(teamId) && active,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<ReminderListResponse>(teamRemindersUrl(teamId as string)),
  });
  return toResult(query);
}

export interface SetReminderArgs {
  /** When to nudge — a FUTURE ISO instant (a past value is a 422 server-side). */
  remind_at: string;
  /**
   * WHO the nudge is for (a membership id) — omit / null = whoever can act on
   * it (§A6). A list of one target, never a count or score about a person (§A9).
   */
  target_membership_id?: string | null;
  /** The optional calm, factual nudge message. */
  note?: string | null;
}

/**
 * Set a forward reminder on a node (POST → 201). Invalidates both reminder
 * scopes so the node list AND the team bell re-render with the new nudge. A
 * past `remind_at` is a 422 — the compose box validates up front, so this is
 * belt-and-braces. Sends only the fields that are present (no `null` noise).
 */
export function useSetReminder(teamId: string | undefined, nodeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ remind_at, target_membership_id, note }: SetReminderArgs) => {
      const body: CreateReminderRequest = { remind_at };
      if (target_membership_id) body.target_membership_id = target_membership_id;
      const trimmedNote = note?.trim();
      if (trimmedNote) body.note = trimmedNote;
      return apiFetch<ReminderResponse>(nodeRemindersUrl(teamId as string, nodeId as string), {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_REMINDERS_SCOPE(teamId ?? "") }),
  });
}

/**
 * Clear (resolve) a reminder by its id (DELETE → 204). Invalidates both scopes
 * so the nudge drops from the node list AND the team bell on success. The
 * backend permits this only for the reminder's setter or a lead / co_lead (403
 * otherwise); the UI only shows the clear affordance to those actors, so a 403
 * should not normally be reachable.
 */
export function useClearReminder(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reminderId: string) =>
      apiFetch<void>(`${teamRemindersUrl(teamId as string)}/${reminderId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_REMINDERS_SCOPE(teamId ?? "") }),
  });
}
